'use client';

import React, { useEffect, useState, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
    TbAdjustmentsHorizontal as SlidersHorizontal,
    TbArrowLeft as ArrowLeft,
    TbArrowRight as ArrowRight,
    TbClock as Clock,
    TbSearch as Search,
    TbUser as User,
} from 'react-icons/tb';
import ScrollReveal from '@/app/components/animations/ScrollReveal';
import { getMediaUrl } from '@/app/lib/media';

interface BlogListClientProps {
  blogs: any[];
  locale: string;
  isAr: boolean;
  t: {
    readMore: string;
    noPosts: string;
    by: string;
    searchPlaceholder?: string;
    sortLatest?: string;
    sortOldest?: string;
  };
}

type Category = { slug: string; label: string; count: number };

// A post's category is a populated service ({ slug, navLabel }); anything
// else (unset, or an unpopulated id) means "no category".
const categoryOf = (post: any): { slug: string; label: string } | null => {
  const c = post?.category;
  return c && typeof c === 'object' && c.slug ? { slug: c.slug, label: c.navLabel || c.slug } : null;
};

const formatDate = (dateStr: string, isAr: boolean) => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(isAr ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

export default function BlogListClient({ blogs, locale, isAr, t }: BlogListClientProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'latest' | 'oldest'>('latest');
  const [category, setCategory] = useState<string | null>(null);

  // Only categories that have posts, biggest first.
  const categories = useMemo<Category[]>(() => {
    const bySlug = new Map<string, Category>();
    for (const post of blogs) {
      const c = categoryOf(post);
      if (!c) continue;
      const entry = bySlug.get(c.slug) ?? { ...c, count: 0 };
      entry.count += 1;
      bySlug.set(c.slug, entry);
    }
    return [...bySlug.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [blogs]);

  // ?category=<service-slug> makes a filtered view shareable. Read it after
  // mount (window, not useSearchParams, so the page stays statically rendered).
  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('category');
    if (fromUrl && categories.some((c) => c.slug === fromUrl)) setCategory(fromUrl);
  }, [categories]);

  const selectCategory = (slug: string | null) => {
    setCategory(slug);
    const url = new URL(window.location.href);
    if (slug) url.searchParams.set('category', slug);
    else url.searchParams.delete('category');
    window.history.replaceState(null, '', url);
  };

  const filteredAndSortedBlogs = useMemo(() => {
    let result = [...blogs];

    if (category) {
      result = result.filter((post) => categoryOf(post)?.slug === category);
    }

    // Search filter
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        (post) =>
          post.title?.toLowerCase().includes(lowerSearch) ||
          post.excerpt?.toLowerCase().includes(lowerSearch)
      );
    }

    // Sort
    result.sort((a, b) => {
      const dateA = new Date(a.publishedAt || 0).getTime();
      const dateB = new Date(b.publishedAt || 0).getTime();
      return sortBy === 'latest' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [blogs, searchTerm, sortBy, category]);

  return (
    <div className="w-full flex flex-col gap-10">
      {/* Search and Filter Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/[0.02] border border-white/10 rounded-2xl p-4 md:p-6 shadow-lg backdrop-blur-sm z-10 relative">
        <div className="relative w-full md:w-96 group">
          <Search className={`absolute ${isAr ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 w-5 h-5 text-brand-text/40 group-focus-within:text-brand-cta transition-colors`} />
          <input
            type="text"
            placeholder={t.searchPlaceholder || (isAr ? 'ابحث في المدونة...' : 'Search blogs...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full bg-brand-bg/50 border border-white/10 focus:border-brand-cta/50 rounded-xl py-3 ${isAr ? 'pr-12 pl-4' : 'pl-12 pr-4'} text-brand-text placeholder-brand-text/40 outline-none transition-all`}
          />
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2 text-brand-text/60 bg-brand-bg/50 px-4 py-3 rounded-xl border border-white/10 w-full md:w-auto">
            <SlidersHorizontal className="w-4 h-4" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'latest' | 'oldest')}
              className="bg-transparent text-brand-text outline-none cursor-pointer w-full md:w-auto"
            >
              <option value="latest" className="bg-brand-bg">{t.sortLatest || (isAr ? 'الأحدث' : 'Latest')}</option>
              <option value="oldest" className="bg-brand-bg">{t.sortOldest || (isAr ? 'الأقدم' : 'Oldest')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Category filter */}
      {categories.length > 0 && (
        <div
          role="group"
          aria-label={isAr ? 'تصفية حسب التصنيف' : 'Filter by category'}
          className="flex flex-wrap gap-2.5 -mt-4"
        >
          {[{ slug: null as string | null, label: isAr ? 'الكل' : 'All', count: blogs.length }, ...categories].map((c) => {
            const active = category === c.slug;
            return (
              <button
                key={c.slug ?? 'all'}
                type="button"
                aria-pressed={active}
                onClick={() => selectCategory(c.slug)}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-body transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta/60 ${
                  active
                    ? 'border-brand-cta bg-brand-cta text-white'
                    : 'border-white/10 bg-brand-bg/50 text-brand-text/75 hover:border-brand-cta/40 hover:text-white'
                }`}
              >
                <span>{c.label}</span>
                <span className={`tabular-nums text-xs ${active ? 'text-white/80' : 'text-brand-text/45'}`}>{c.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Blogs Grid */}
      {filteredAndSortedBlogs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {filteredAndSortedBlogs.map((post, i) => {
            const imageUrl = getMediaUrl(post.featuredImage, 'card') || getMediaUrl(post.featuredImage);
            return (
              <ScrollReveal
                key={post.slug}
                mode="fade-up"
                delay={(i % 10) * 0.08}
                className="group relative flex flex-col h-full bg-white/[0.02] border border-white/10 hover:border-brand-cta/40 hover:bg-white/[0.04] rounded-[2rem] overflow-hidden transition-all duration-500 shadow-lg"
              >
                {/* Image wrapper with zoom on hover */}
                <div className="relative aspect-video overflow-hidden rounded-t-[2rem] bg-black/30">
                  {imageUrl ? (
                    <Image
                      src={imageUrl}
                      alt={post.title}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      className="object-cover transition-transform duration-700 ease-[0.25,1,0.5,1] group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-brand-cta/10 to-brand-bg flex items-center justify-center text-brand-cta/40 uppercase font-heading tracking-widest text-sm">
                      nibras Digital
                    </div>
                  )}
                </div>

                {/* Body Content */}
                <div className="flex flex-col flex-1 p-6 md:p-8 space-y-4 z-10 relative">
                  {/* Meta info */}
                  <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-brand-text/50 font-body">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-brand-cta" />
                      {formatDate(post.publishedAt, isAr)}
                    </span>
                    <span className="w-1.5 h-1.5 bg-white/20 rounded-full" />
                    <span className="flex items-center gap-1.5">
                      <User className="w-4 h-4 text-brand-cta" />
                      {post.author || 'nibras'}
                    </span>
                  </div>

                  {/* Category */}
                  {categoryOf(post) && (
                    <button
                      type="button"
                      onClick={() => selectCategory(categoryOf(post)!.slug)}
                      className="self-start text-xs font-heading font-bold uppercase tracking-wider text-brand-cta hover:text-white transition-colors"
                    >
                      {categoryOf(post)!.label}
                    </button>
                  )}

                  {/* Title */}
                  <h3 className="text-xl md:text-2xl font-bold font-heading text-white leading-tight group-hover:text-brand-cta transition-colors duration-300 line-clamp-2">
                    {/* The title is the link Google reads; "Read Article" alone says nothing about the post. */}
                    <Link href={`/${locale}/blogs/${post.slug}`}>{post.title}</Link>
                  </h3>

                  {/* Excerpt */}
                  <p className="text-sm md:text-base text-brand-text/65 leading-relaxed font-body line-clamp-3 flex-1">
                    {post.excerpt}
                  </p>

                  {/* Read More Link */}
                  <div className="pt-4 border-t border-white/5 mt-auto">
                    <Link
                      href={`/${locale}/blogs/${post.slug}`}
                      className="inline-flex items-center gap-2 text-brand-cta group-hover:text-white transition-colors duration-300 font-heading text-sm md:text-base font-bold uppercase tracking-wider"
                    >
                      <span>{t.readMore}</span>
                      {isAr ? (
                        <ArrowLeft className="w-4 h-4 transform group-hover:-translate-x-1.5 transition-transform duration-300" />
                      ) : (
                        <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1.5 transition-transform duration-300" />
                      )}
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      ) : (
        <ScrollReveal mode="fade-up" className="text-center py-20 bg-white/[0.01] border border-white/5 rounded-[2rem] p-8 max-w-xl mx-auto backdrop-blur-md">
          <p className="text-xl text-brand-text/60 font-body">{searchTerm ? (isAr ? 'لا توجد نتائج بحث مطابقة.' : 'No search results found.') : t.noPosts}</p>
        </ScrollReveal>
      )}
    </div>
  );
}
